import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { mergeExistingFields } from "helpers/mergeExistingFields";

import instance from "api/apiClient";
import { apiRoutes } from "configs/apiRoutes";
import { ApiErrorPayload } from "types/ApiErrorPayload";

export interface SeoItem {
  id?: number;
  title: string;
  type: string;
  isActive: boolean;
}

export interface SeoAttributes {
  geo: string;
  locale: string;
  headTitle?: string | null;
  metaDescription?: string | null;
  openGraphTitle?: string | null;
  openGraphDescription?: string | null;
  twitterTitle?: string | null;
  twitterDescription?: string | null;
  fullDescription?: string | null;
  additionalDescription?: string | null;
}

interface SeoState {
  list: SeoItem[];
  activePage: number;
  totalPages: number;
  totalItems: number;
  isListFetched: boolean;
  isItemFetching: boolean;
  isItemFetched: boolean;
  payload?: SeoItem & Partial<SeoAttributes>;
  types: { value: string; label: string }[];
  isTypesFetched: boolean;
  processingItemId?: number;
}

interface GetSeoListPayload {
  limit: number;
  offset: number;
  title?: string;
  types?: string[];
  isActive?: number;
}
const defPayload: SeoItem = {
  title: "",
  type: "",
  isActive: true,
};

const initialState: SeoState = {
  list: [],
  activePage: 1,
  totalItems: 0,
  totalPages: 0,
  types: [],
  isTypesFetched: false,
  isItemFetching: false,
  isListFetched: false,
  isItemFetched: false,
  payload: defPayload,
};

export const getSeoItem = createAsyncThunk("seo/getOne", async (id: string) => {
  const res = await instance.get(`${apiRoutes.seoGet}/${id}`);
  return res.data;
});

export const getSeoList = createAsyncThunk(
  "seo/list",
  async (data: GetSeoListPayload) => {
    try {
      const result = await instance.post(apiRoutes.seoList, data);
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);

export const getSeoTypes = createAsyncThunk("seo/types", async () => {
  try {
    const res = await instance.get(apiRoutes.seoTypes);
    return res.data.pages;
  } catch {
    return [];
  }
});

export const createSeo = createAsyncThunk<
  number,
  SeoItem,
  { rejectValue: ApiErrorPayload }
>("seo/create", async (data: SeoItem, { rejectWithValue }) => {
  try {
    const response = await instance.post(apiRoutes.seoCreate, {
      ...data,
      isActive: data.isActive ? 1 : 0,
    });

    return response.data.id;
  } catch (error: any) {
    const apiError = error?.data?.error;

    if (apiError?.code) {
      return rejectWithValue(apiError);
    }

    return rejectWithValue({
      code: "InternalError",
      fieldErrors: [],
      globalErrors: [{ code: "unknownError" }],
    });
  }
});

export const updateSeo = createAsyncThunk<
  boolean,
  SeoItem,
  { rejectValue: ApiErrorPayload }
>("seo/update", async (data: SeoItem, { rejectWithValue }) => {
  try {
    await instance.put(`${apiRoutes.seoUpdate}/${data.id}`, {
      ...data,
      isActive: data.isActive ? 1 : 0,
    });

    return true;
  } catch (error: any) {
    const apiError = error?.data?.error;

    if (apiError?.code) {
      return rejectWithValue(apiError);
    }

    return rejectWithValue({
      code: "InternalError",
      fieldErrors: [],
      globalErrors: [{ code: "unknownError" }],
    });
  }
});

export const updateSeoAttributes = createAsyncThunk<
  boolean,
  { id: number; data: SeoAttributes },
  { rejectValue: ApiErrorPayload }
>("seo/updateAttributes", async ({ id, data }, { rejectWithValue }) => {
  try {
    await instance.put(`/cms/pages/seo/attributes/${id}`, data);
    return true;
  } catch (error: any) {
    const apiError = error?.data?.error;

    if (apiError?.code) {
      return rejectWithValue(apiError);
    }

    return rejectWithValue({
      code: "InternalError",
      fieldErrors: [],
      globalErrors: [{ code: "unknownError" }],
    });
  }
});

export const deleteSeo = createAsyncThunk(
  "seo/delete",
  async (id: string | number) => {
    try {
      await instance.delete(`${apiRoutes.seoDelete}/${id}`);
      return true;
    } catch (error) {
      return false;
    }
  },
);

export const cmsSeoSlice = createSlice({
  name: "cmsSeo",
  initialState,
  reducers: {
    handlePaginate: (state, action: PayloadAction<number>) => {
      state.activePage = action.payload;
      state.isListFetched = false;
    },
    handleUpdateFields: (state: SeoState, action: PayloadAction<SeoItem>) => {
      state.payload = { ...state.payload, ...action.payload };
    },

    handleClearFields: (state: SeoState) => {
      state.payload = defPayload;
      state.isItemFetched = false;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(getSeoList.fulfilled, (state, action) => {
      state.isListFetched = true;
      state.list = action.payload?.items ?? [];
      state.totalItems = action.payload?.totalItems ?? 0;
      state.totalPages = action.payload?.totalPages ?? 1;
    });
    builder.addCase(getSeoItem.pending, (state) => {
      state.isItemFetching = true;
    });

    builder.addCase(getSeoItem.fulfilled, (state, action) => {
      state.isItemFetching = false;
      state.isItemFetched = true;
      state.payload = action.payload;
    });

    builder.addCase(getSeoItem.rejected, (state) => {
      state.isItemFetching = false;
      state.isItemFetched = true;
    });

    builder.addCase(getSeoTypes.fulfilled, (state, action) => {
      state.types = action.payload.map((el: any) => ({
        value: el,
        label: el,
      }));
      state.isTypesFetched = true;
    });

    builder.addCase(createSeo.fulfilled, (state) => {
      state.isListFetched = false;
    });

    builder.addCase(updateSeo.pending, (state, action) => {
      state.isItemFetching = true;
      state.processingItemId = action.meta.arg.id;
    });

    builder.addCase(updateSeo.fulfilled, (state, action) => {
      state.list = state.list.map((item) =>
        item.id === action.meta.arg.id
          ? mergeExistingFields(item, action.meta.arg)
          : item,
      );
      state.isItemFetching = false;
      state.processingItemId = undefined;
    });
    builder.addCase(updateSeo.rejected, (state) => {
      state.isItemFetching = false;
      state.processingItemId = undefined;
    });

    builder.addCase(deleteSeo.pending, (state, action) => {
      state.processingItemId = +action.meta.arg;;
    });

    builder.addCase(deleteSeo.fulfilled, (state) => {
      state.isListFetched = false;
      state.processingItemId = undefined;
    });

    builder.addCase(deleteSeo.rejected, (state) => {
      state.processingItemId = undefined;
    });
  },
});

export const { handlePaginate, handleUpdateFields, handleClearFields } =
  cmsSeoSlice.actions;
